import {
  Component,
  input,
  viewChild,
  ElementRef,
  contentChild,
  TemplateRef,
  ChangeDetectionStrategy,
  signal,
  inject,
  NgZone,
  effect,
  OnDestroy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { IconComponent } from 'src/app/components/icon/icon.component';

@Component({
  selector: 'app-carousel',
  imports: [CommonModule, IconComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './carousel.component.html',
  styleUrl: './carousel.component.scss',
})
export class CarouselComponent<T> implements OnDestroy {
  readonly id = input<string | undefined>(undefined);

  items = input.required<T[]>();
  itemWidth = input<string>('280px');

  itemTemplate = contentChild<TemplateRef<unknown>>('carouselItem');

  private readonly scrollContainer = viewChild.required<ElementRef<HTMLElement>>('scrollContainer');

  protected readonly prevIconColor = () => (this.isStart() ? '#cfcfcf' : '#333E48');
  protected readonly nextIconColor = () => (this.isEnd() ? '#cfcfcf' : '#333E48');

  private resizeObserver?: ResizeObserver;
  private readonly ngZone = inject(NgZone);
  private rafId: number | null = null;

  protected isStart = signal(true);
  protected isEnd = signal(false);

  constructor() {
    effect(() => {
      const el = this.scrollContainer()?.nativeElement;
      if (!el) return;

      this.ngZone.runOutsideAngular(() => {
        this.resizeObserver = new ResizeObserver(() => {
          this.scheduleCheck();
        });

        this.resizeObserver.observe(el);

        el.addEventListener('scrollend', () => {
          this.scheduleCheck();
        });
      });

      this.scheduleCheck();
    });

    effect(() => {
      this.items();
      this.scheduleCheck();
    });
  }

  onScroll() {
    this.scheduleCheck();
  }

  private scheduleCheck() {
    if (this.rafId) return;

    this.ngZone.runOutsideAngular(() => {
      this.rafId = requestAnimationFrame(() => {
        this.checkScrollStateLogic();
        this.rafId = null;
      });
    });
  }

  private checkScrollStateLogic() {
    const el = this.scrollContainer()?.nativeElement;
    if (!el) return;

    const { scrollLeft, scrollWidth, clientWidth } = el;
    const tolerance = 2;

    const newIsStart = scrollLeft <= 0;

    const hasOverflow = scrollWidth > clientWidth;
    const reachedEnd = Math.abs(scrollWidth - clientWidth - scrollLeft) < tolerance;

    const newIsEnd = !hasOverflow || reachedEnd;

    if (newIsStart !== this.isStart() || newIsEnd !== this.isEnd()) {
      this.ngZone.run(() => {
        this.isStart.set(newIsStart);
        this.isEnd.set(newIsEnd);
      });
    }
  }

  scroll(direction: 'left' | 'right') {
    const el = this.scrollContainer().nativeElement;
    const widthVal = parseInt(this.itemWidth().replace('px', ''), 10) || 280;
    const gap = 16;
    const scrollAmount = widthVal + gap;

    el.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth',
    });
  }

  ngOnDestroy() {
    this.resizeObserver?.disconnect();
    if (this.rafId) cancelAnimationFrame(this.rafId);
  }

  trackByIndex = (index: number, item: T) => (item as any).id ?? (item as any).uuid ?? index;
}
